import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import MobileNav from '../../components/mobile/MobileNav';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  User,
  Lock,
  LogOut,
  ClipboardCheck,
  Wrench,
  AlertTriangle,
  Clock,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const MobileProfile = () => {
  const navigate = useNavigate();
  const { user, logout, getToken } = useMobileAuth();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showPinChange, setShowPinChange] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = getToken();
        const res = await mobileApi.getProfile(token);
        setProfile(res.data);
      } catch {
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handlePinChange = async () => {
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      toast.error('PIN must be 4 digits');
      return;
    }
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    setPinLoading(true);
    try {
      const token = getToken();
      await mobileApi.setupPin(newPin, token);
      localStorage.setItem('mobile_pin_enabled', 'true');
      toast.success('PIN updated successfully');
      setShowPinChange(false);
      setNewPin('');
      setConfirmPin('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PIN');
    } finally {
      setPinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" />
      </div>
    );
  }

  const stats = profile?.stats || {};

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-20" data-testid="mobile-profile-page">
      <MobileHeader title="Profile" showBack />

      <div className="px-4 py-4">
        {/* Avatar + Info */}
        <div className="text-center mb-6">
          <div className="mx-auto w-20 h-20 bg-[var(--surface-sunken)] rounded-full flex items-center justify-center text-2xl font-bold text-[var(--volt-icon)] mb-3">
            {(profile?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <h2 className="text-xl font-bold text-[var(--ink-primary)]">{profile?.name}</h2>
          <p className="text-[var(--ink-tertiary)] text-sm capitalize">{profile?.role}</p>
          {profile?.depot_name && (
            <p className="text-[var(--ink-tertiary)] text-sm">{profile.depot_name}</p>
          )}
        </div>

        {/* Quick Access */}
        <div className="mb-6">
          <MobileCard
            className="mb-2"
            onClick={() => setShowPinChange(!showPinChange)}
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-[var(--ink-tertiary)]" />
              <div className="flex-1">
                <p className="font-medium text-[var(--ink-primary)]">
                  {profile?.has_pin ? 'Change PIN' : 'Set Up PIN'}
                </p>
                <p className="text-xs text-[var(--ink-tertiary)]">
                  {profile?.has_pin ? 'Update your quick access PIN' : 'Enable quick PIN login'}
                </p>
              </div>
            </div>
          </MobileCard>

          {showPinChange && (
            <MobileCard className="mb-2">
              <div className="space-y-3">
                <div>
                  <Label className="text-[var(--ink-secondary)] text-sm">New PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={newPin}
                    onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center text-xl tracking-[0.5em] bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--ink-primary)] h-12"
                    data-testid="new-pin-input"
                  />
                </div>
                <div>
                  <Label className="text-[var(--ink-secondary)] text-sm">Confirm PIN</Label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={confirmPin}
                    onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="text-center text-xl tracking-[0.5em] bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--ink-primary)] h-12"
                  />
                </div>
                <Button
                  onClick={handlePinChange}
                  disabled={pinLoading || newPin.length !== 4 || confirmPin.length !== 4}
                  className="w-full bg-[var(--volt)] hover:bg-[var(--volt)] text-black"
                >
                  {pinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save PIN'}
                </Button>
              </div>
            </MobileCard>
          )}
        </div>

        {/* Stats */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-[var(--ink-tertiary)] uppercase tracking-wide mb-3">
            This Month
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <MobileCard padding="small" className="text-center">
              <Wrench className="w-5 h-5 text-[var(--neutral-ink)] mx-auto mb-1" />
              <p className="text-xl font-bold text-[var(--ink-primary)]">{stats.work_orders_completed || 0}</p>
              <p className="text-xs text-[var(--ink-tertiary)]">Work Orders</p>
            </MobileCard>
            <MobileCard padding="small" className="text-center">
              <ClipboardCheck className="w-5 h-5 text-[var(--pass-ink)] mx-auto mb-1" />
              <p className="text-xl font-bold text-[var(--ink-primary)]">{stats.inspections_performed || 0}</p>
              <p className="text-xs text-[var(--ink-tertiary)]">Inspections</p>
            </MobileCard>
            <MobileCard padding="small" className="text-center">
              <AlertTriangle className="w-5 h-5 text-[var(--volt-icon)] mx-auto mb-1" />
              <p className="text-xl font-bold text-[var(--ink-primary)]">{stats.defects_reported || 0}</p>
              <p className="text-xs text-[var(--ink-tertiary)]">Defects</p>
            </MobileCard>
            <MobileCard padding="small" className="text-center">
              <Clock className="w-5 h-5 text-[var(--neutral-ink)] mx-auto mb-1" />
              <p className="text-xl font-bold text-[var(--ink-primary)]">{stats.hours_logged || 0}</p>
              <p className="text-xs text-[var(--ink-tertiary)]">Hours</p>
            </MobileCard>
          </div>
        </div>

        {/* Logout */}
        <Button
          onClick={logout}
          variant="outline"
          className="w-full h-12 border-[var(--critical-edge)] text-[var(--critical-ink)] hover:bg-[var(--critical-bg)]"
          data-testid="mobile-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log Out
        </Button>
      </div>

      <MobileNav />
    </div>
  );
};

export default MobileProfile;
